import { Controller } from "@hotwired/stimulus"
import {
  applyParticipantLaborUnits as fillParticipantLaborUnits,
  namedParticipantIdsFromRows,
  reorderParticipantRows,
  syncSupplyExitContractorSelects
} from "../helpers/assignment_participants.js"
import { peopleForOpenAssignmentDate } from "../helpers/open_assignment_last_people.js"

export default class extends Controller {
  static targets = [
    "logDate",
    "unitsField",
    "unitsLabel",
    "unitsInput",
    "taskPriceField",
    "taskPriceInput",
    "laborUnitsField",
    "laborUnitsInput",
    "laborRateField",
    "laborRateInput",
    "groupParticipants",
    "formulaLabel",
    "totalCost",
    "supplyExitList",
    "supplyExitTemplate",
    "supplyExitContractorField",
    "supplyExitQuantityLabel",
    "supplyExitContractor"
  ]

  static values = {
    costMethod: { type: String, default: "task_unit" },
    laborDay: { type: Boolean, default: false },
    taskMeta: { type: Object, default: {} },
    contractorGroupMemberIds: { type: Object, default: {} },
    contractorLaborRates: { type: Object, default: {} },
    openAssignmentLastPeople: { type: Object, default: {} },
    defaultUnitsLabel: { type: String, default: "" },
    taskUnitFormula: { type: String, default: "" },
    laborUnitFormula: { type: String, default: "" },
    currency: String,
    currencyDecimalPlaces: { type: Number, default: 2 },
    currencyFormat: String,
    currencySubunitToUnit: { type: Number, default: 100 },
    currencySymbol: String,
    decimalSeparator: String,
    delimiter: String
  }

  connect() {
    this.taskKey = this.#selectedFieldTaskId()
    this.assigneeKey = this.#selectedAssigneeValue()
    this.#applyTaskMeta(this.#selectedFieldTaskId())
    this.syncAssigneeFields({ resetSelection: false })
    this.mirrorQuantityToLaborUnits()
  }

  setDatePreset(event) {
    const daysAgo = Number(event.params.daysAgo || 0)
    const input = this.logDateTarget
    const now = new Date()
    now.setDate(now.getDate() - daysAgo)
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    const hours = String(now.getHours()).padStart(2, "0")
    const minutes = String(now.getMinutes()).padStart(2, "0")
    input.value = `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // new-field-task:change carries a new Field Task's cost meta (ADR 0090), or null for an existing one.
  fieldTaskChanged(event) {
    if (event?.type === "new-field-task:change") this.newTaskMeta = event.detail
    const taskKey = this.newTaskMeta ? "new" : this.#selectedFieldTaskId()
    const taskChanged = taskKey !== this.taskKey
    this.taskKey = taskKey

    this.#applyTaskMeta(this.#selectedFieldTaskId())
    this.syncCostFields()
    this.#refreshParticipantLaborControls()
    if (taskChanged && this.openAssignee()) this.#refreshAssignmentParticipants(true)
    this.#syncSupplyExitContractors()
    this.mirrorQuantityToLaborUnits()
  }

  coverageChanged() {
    if (this.openAssignee()) this.#refreshAssignmentParticipants(true)
    this.#syncSupplyExitContractors()
    this.updateTotalCost()
  }

  // new-assignee:change fires on every change of the assignee picker, with the new name's kind or null; only a new
  // pick resets the participants.
  assigneeChanged(event) {
    this.newAssignee = event?.detail?.assignee || null
    const assigneeKey = this.#selectedAssigneeValue()
    if (assigneeKey === this.assigneeKey) return

    this.assigneeKey = assigneeKey
    this.syncAssigneeFields({ resetSelection: !event?.detail?.connecting })
  }

  personAdded() {
    this.#refreshParticipantLaborControls()
    this.#syncSupplyExitContractors()
    this.updateTotalCost()
  }

  namedPeopleChanged(event) {
    this.#defaultOpenAssignmentJornales(event.currentTarget?.closest("[data-participant-row]"))
    this.#syncSupplyExitContractors()
    this.updateTotalCost()
  }

  applyParticipantLaborUnits(event) {
    const panel = event.target.closest("[data-assignment-participants]")
    if (!panel) return

    fillParticipantLaborUnits(panel)
    this.updateTotalCost()
  }

  addSupplyExit(event) {
    event.preventDefault()
    const template = this.supplyExitTemplateTarget
    const clone = template.content.cloneNode(true)
    this.supplyExitListTarget.appendChild(clone)
    this.syncAssigneeFields({ resetSelection: false })
  }

  removeSupplyExit(event) {
    event.preventDefault()
    event.currentTarget.closest("[data-supply-exit-row]")?.remove()
  }

  supplyExitSupplyChanged(event) {
    const select = event.currentTarget
    const row = select.closest("[data-supply-exit-row]")
    const label = row?.querySelector("[data-field-task-quick-log-target='supplyExitQuantityLabel']")
    if (!label) return

    const selectedOption = select.options[select.selectedIndex]
    const unitName = selectedOption?.dataset?.consumptionUnit
    label.textContent = unitName || label.dataset.default || ""
  }

  syncCostFields() {
    const taskUnit = this.costMethodValue === "task_unit"
    const laborUnit = !taskUnit
    const laborDay = this.laborDayValue
    const hideUnitsForNamedJornal = laborUnit && laborDay && this.namedPeopleAssignee()
    const showLaborUnits = laborUnit && !this.namedPeopleAssignee() && !laborDay

    this.toggle(this.hasUnitsFieldTarget ? this.unitsFieldTarget : null, !hideUnitsForNamedJornal)
    this.toggle(this.hasTaskPriceFieldTarget ? this.taskPriceFieldTarget : null, taskUnit)
    this.toggle(this.hasLaborUnitsFieldTarget ? this.laborUnitsFieldTarget : null, showLaborUnits)
    this.toggle(this.hasLaborRateFieldTarget ? this.laborRateFieldTarget : null, laborUnit && !this.namedPeopleAssignee())
    this.#syncFormulaLabel()
  }

  mirrorQuantityToLaborUnits() {
    if (this.laborDayValue && this.costMethodValue === "labor_unit" && this.hasUnitsInputTarget && this.hasLaborUnitsInputTarget) {
      this.laborUnitsInputTarget.value = this.unitsInputTarget.value
    }

    this.updateTotalCost()
  }

  syncAssigneeFields({ resetSelection = false } = {}) {
    this.#fillLaborRateFromAssignee()
    this.#refreshAssignmentParticipants(resetSelection)
    this.element.querySelectorAll("[data-field-task-quick-log-target='supplyExitContractorField']").forEach((el) => {
      this.toggle(el, this.namedPeopleAssignee())
    })
    this.syncCostFields()
    this.#syncSupplyExitContractors()
    this.updateTotalCost()
  }

  updateTotalCost() {
    if (!this.hasTotalCostTarget) return

    const cents = this.#totalCostCents()
    if (cents === null) {
      this.totalCostTarget.textContent = "—"
      return
    }

    this.totalCostTarget.textContent = this.#formatMoney(cents)
  }

  groupAssignee() {
    return (this.#selectedAssigneeValue() || "").startsWith("contractor_group:")
  }

  openAssignee() {
    return this.#selectedAssigneeValue() === "open"
  }

  namedPeopleAssignee() {
    return this.groupAssignee() || this.openAssignee()
  }

  toggle(element, show) {
    if (!element) return
    element.classList.toggle("hidden", !show)
  }

  #applyTaskMeta(taskId) {
    const meta = this.newTaskMeta || this.taskMetaValue[String(taskId)] || {}
    this.costMethodValue = meta.costMethod || "task_unit"
    this.laborDayValue = Boolean(meta.laborDay)

    if (this.hasUnitsLabelTarget) {
      this.unitsLabelTarget.textContent =
        meta.unitOther || meta.unitOne || this.unitsLabelTarget.dataset.default || this.defaultUnitsLabelValue
    }

    if (this.hasTaskPriceInputTarget && meta.defaultPrice && !this.taskPriceInputTarget.value) {
      this.taskPriceInputTarget.value = meta.defaultPrice
    }
  }

  #fillLaborRateFromAssignee() {
    if (!this.hasLaborRateInputTarget) return

    this.laborRateInputTarget.value = this.contractorLaborRatesValue[this.#selectedAssigneeValue()] || ""
  }

  #refreshAssignmentParticipants(resetSelection = false) {
    if (!this.hasGroupParticipantsTarget) return

    const panel = this.groupParticipantsTarget
    const groupId = this.#selectedContractorGroupId()
    const show = this.namedPeopleAssignee()
    panel.classList.toggle("hidden", !show)
    panel.open = show
    this.#setParticipantInputsDisabled(!show)
    this.#refreshParticipantLaborControls()

    if (!show) {
      panel.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
        checkbox.checked = false
      })
      return
    }

    const memberIds = new Set(
      (this.contractorGroupMemberIdsValue[String(groupId)] || []).map(String)
    )

    if (resetSelection) {
      const lastPeople = this.openAssignee() ? this.#lastOpenAssignmentPeople() : []
      const lastIds = new Set(lastPeople.map((person) => String(person.id)))
      const lastUnits = Object.fromEntries(
        lastPeople.map((person) => [String(person.id), person.laborUnits])
      )

      panel.querySelectorAll("[data-participant-row]").forEach((row) => {
        const checkbox = row.querySelector("input[type='checkbox']")
        const contractorIdInput = row.querySelector('input[name*="[contractor_id]"]')
        if (!checkbox || !contractorIdInput) return

        const contractorId = String(contractorIdInput.value)
        checkbox.checked = this.groupAssignee()
          ? memberIds.has(contractorId)
          : lastIds.has(contractorId)

        const laborUnitsInput = row.querySelector("[data-participant-labor-units-input]")
        if (this.openAssignee() && laborUnitsInput) {
          laborUnitsInput.value = lastUnits[contractorId] ?? 1
        }
      })
    }

    if (this.groupAssignee()) {
      reorderParticipantRows(panel, memberIds)
    }
  }

  #setParticipantInputsDisabled(disabled) {
    if (!this.hasGroupParticipantsTarget) return

    this.groupParticipantsTarget.querySelectorAll("input").forEach((input) => {
      input.disabled = disabled
    })
  }

  #refreshParticipantLaborControls() {
    if (!this.hasGroupParticipantsTarget) return

    const panel = this.groupParticipantsTarget
    const showLaborControls = this.costMethodValue === "labor_unit" && this.namedPeopleAssignee()
    const laborUnitGrid = "sm:grid-cols-[minmax(0,1fr)_7rem_8rem]"

    panel.querySelectorAll("[data-participant-labor-controls]").forEach((element) => {
      element.classList.toggle("hidden", !showLaborControls)
    })
    panel.querySelectorAll("[data-participant-rate-controls]").forEach((element) => {
      element.classList.toggle("hidden", !showLaborControls)
    })
    panel.querySelectorAll("[data-participant-row]").forEach((participantRow) => {
      participantRow.classList.toggle("sm:grid-cols-1", !showLaborControls)
      participantRow.classList.toggle(laborUnitGrid, showLaborControls)
    })
  }

  #syncFormulaLabel() {
    if (!this.hasFormulaLabelTarget) return

    this.formulaLabelTarget.textContent =
      this.costMethodValue === "labor_unit" ? this.laborUnitFormulaValue : this.taskUnitFormulaValue
  }

  #totalCostCents() {
    if (this.costMethodValue === "labor_unit") {
      if (this.namedPeopleAssignee()) return this.#participantTotalCostCents()

      const rate = this.#laborRateCents()
      if (rate === null) return null

      return Math.round(this.#laborUnits() * rate)
    }

    const unitPrice = this.#taskUnitPriceCents()
    if (unitPrice === null) return null

    return Math.round(this.#quantity() * unitPrice)
  }

  #participantTotalCostCents() {
    let total = 0

    for (const row of this.#participantRows()) {
      const checkbox = row.querySelector("input[type='checkbox']")
      if (!checkbox?.checked || checkbox.disabled) continue

      const laborUnits = this.#numberFromInput(row.querySelector("[data-participant-labor-units-input]"))
      const laborRate = this.#moneyCentsFromInput(row.querySelector("[data-participant-labor-rate-input]"))
      if (laborRate === null) return null

      total += laborUnits * laborRate
    }

    return Math.round(total)
  }

  #quantity() {
    if (!this.hasUnitsInputTarget) return 0

    return this.#numberFromInput(this.unitsInputTarget)
  }

  #laborUnits() {
    if (!this.hasLaborUnitsInputTarget) return 0

    return this.#numberFromInput(this.laborUnitsInputTarget)
  }

  #laborRateCents() {
    if (!this.hasLaborRateInputTarget) return null

    return this.#moneyCentsFromInput(this.laborRateInputTarget)
  }

  #taskUnitPriceCents() {
    if (!this.hasTaskPriceInputTarget) return null

    return this.#moneyCentsFromInput(this.taskPriceInputTarget)
  }

  #participantRows() {
    if (!this.hasGroupParticipantsTarget) return []

    return Array.from(this.groupParticipantsTarget.querySelectorAll("[data-participant-row]"))
  }

  #numberFromInput(input) {
    if (!input) return 0

    const value = input.value.trim()
    if (value === "") return 0

    const quantity = Number(value)
    return Number.isFinite(quantity) ? quantity : 0
  }

  #moneyCentsFromInput(input) {
    if (!input) return 0

    const value = input.value.trim()
    if (value === "") return 0

    const amount = Number(this.#normalizeMoneyValue(value))
    if (!Number.isFinite(amount)) return null

    return Math.round(amount * this.#currencySubunitToUnit())
  }

  #normalizeMoneyValue(value) {
    let normalized = value.replace(/[^\d.,-]/g, "")
    const delimiter = this.delimiterValue
    const decimalSeparator = this.decimalSeparatorValue

    if (delimiter) {
      normalized = normalized.replace(new RegExp(this.#escapeRegExp(delimiter), "g"), "")
    }

    if (decimalSeparator && decimalSeparator !== ".") {
      normalized = normalized.replace(new RegExp(this.#escapeRegExp(decimalSeparator), "g"), ".")
    }

    return normalized
  }

  #escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  #formatMoney(cents) {
    const amount = cents / this.#currencySubunitToUnit()
    const decimalPlaces = this.#currencyDecimalPlaces()
    const sign = amount < 0 ? "-" : ""
    const [integerPart, decimalPart] = Math.abs(amount).toFixed(decimalPlaces).split(".")
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, this.delimiterValue)
    const formattedNumber = decimalPlaces > 0
      ? `${sign}${formattedInteger}${this.decimalSeparatorValue}${decimalPart}`
      : `${sign}${formattedInteger}`
    const format = this.currencyFormatValue || "%u%n"

    return format
      .replace("%u", this.currencySymbolValue || this.currencyValue)
      .replace("%n", formattedNumber)
  }

  #currencySubunitToUnit() {
    return this.currencySubunitToUnitValue > 0 ? this.currencySubunitToUnitValue : 100
  }

  #currencyDecimalPlaces() {
    return this.currencyDecimalPlacesValue >= 0 ? this.currencyDecimalPlacesValue : 2
  }

  #selectedFieldTaskId() {
    const input = this.element.querySelector('input[name="field_task_quick_log[field_task_id]"]')
    return input?.value || ""
  }

  #selectedLotId() {
    const input = this.element.querySelector('input[name="field_task_quick_log[lot_id]"]')
    return input?.value || ""
  }

  #lastOpenAssignmentPeople() {
    return peopleForOpenAssignmentDate(
      this.openAssignmentLastPeopleValue,
      this.#selectedFieldTaskId(),
      this.#selectedLotId(),
      this.hasLogDateTarget ? this.logDateTarget.value.slice(0, 10) : ""
    )
  }

  #defaultOpenAssignmentJornales(row) {
    if (!this.openAssignee() || !row) return

    const checkbox = row.querySelector("input[type='checkbox']")
    const laborUnitsInput = row.querySelector("[data-participant-labor-units-input]")
    if (!checkbox?.checked || !laborUnitsInput) return
    if (this.#numberFromInput(laborUnitsInput) === 0) laborUnitsInput.value = "1"
  }

  #syncSupplyExitContractors() {
    if (!this.openAssignee()) return

    syncSupplyExitContractorSelects(
      this.supplyExitContractorTargets,
      namedParticipantIdsFromRows(this.#participantRows())
    )
  }

  // A new assignee answered as a contractor or a group acts like one with no members or rate yet.
  #selectedAssigneeValue() {
    if (this.newAssignee) return this.newAssignee.kind ? `${this.newAssignee.kind}:new` : ""

    const input = this.element.querySelector('input[name="field_task_quick_log[assignment_target]"]')
    return input?.value || ""
  }

  #selectedContractorGroupId() {
    const value = this.#selectedAssigneeValue()
    if (!value.startsWith("contractor_group:")) return ""
    return value.replace("contractor_group:", "")
  }
}
